import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@reach/router";
import styled from "styled-components";
import { NavigationProps } from "../utils/navigationProps";
import { usePageTitle } from "../hooks/usePageTitle";
import { ThemeProps } from "../styles/theme";
import { Content, Message, Page, PageTitle, PrimaryButton } from "../train/trainUi";
import { CarCardResult, CarLimit, CarMode, CarSession, CarSnapshot, CarStep } from "./carSession";
import { CarGrade } from "./carGrade";
import { listMicrophones } from "./voice/micCapture";
import { createSpeechInput, SpeechStatus } from "./voice/speechInput";

const LIMITS: CarLimit[] = [10, 20, 30, 'all'];
const MICROPHONE_KEY = 'memkeep.car.microphone';

const STEP_LABELS: Record<CarStep, string> = {
    starting: 'Starting…',
    loading: 'Loading…',
    intro: 'Next card',
    question: 'Question',
    listen: 'Your answer',
    feedback: 'Answer',
    announce: '',
    end: 'More cards?',
    finished: 'Finished',
};

const SPEECH_LABELS: Record<SpeechStatus, string> = {
    off: 'Microphone off',
    connecting: 'Connecting to speech recognition…',
    listening: '🎙 Listening',
    error: 'Speech recognition failed',
};

const GRADE_LABELS: Record<CarGrade, string> = {
    good: 'Good',
    hard: 'Hard',
    again: 'Again',
};

/** What can be said, shown on the start screen and while driving. */
const COMMANDS: [string, string][] = [
    ['«дальше»', 'next item (not counted)'],
    ['«следующая карточка»', 'skip the rest of the card'],
    ['«повтори» / «медленнее»', 'read it again'],
    ['«не знаю»', 'hear the answer (counts as a mistake)'],
    ['«пауза» / «продолжай»', 'pause and resume'],
    ['«сколько осталось»', 'progress'],
    ['«закончить»', 'end the session'],
];

function loadMicrophone(): string {
    try {
        return window.localStorage.getItem(MICROPHONE_KEY) ?? '';
    } catch {
        return '';
    }
}

function saveMicrophone(deviceId: string): void {
    try {
        window.localStorage.setItem(MICROPHONE_KEY, deviceId);
    } catch {
        // Private mode: the choice just isn't remembered.
    }
}

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
`;

const Label = styled.div<ThemeProps>`
    font-size: 13px;
    font-weight: 600;
    color: ${p => p.theme.secondaryText};
`;

const Options = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const Option = styled.button<ThemeProps & { $active: boolean }>`
    padding: 10px 18px;
    font-size: 15px;
    font-family: inherit;
    border-radius: 6px;
    cursor: pointer;
    color: ${p => p.$active ? p.theme.successButtonForeground : p.theme.neutralButtonForeground};
    background-color: ${p => p.$active ? p.theme.successButtonBackground : p.theme.neutralButtonBackground};
    border: 1px solid ${p => p.$active ? p.theme.successButtonBorder : p.theme.neutralButtonBorder};
`;

const Select = styled.select<ThemeProps>`
    padding: 10px;
    font-size: 15px;
    font-family: inherit;
    border-radius: 6px;
    max-width: 100%;
    color: ${p => p.theme.inputText};
    background-color: ${p => p.theme.inputBackground};
    border: 1px solid ${p => p.theme.inputBorder};
`;

const Hint = styled.p<ThemeProps>`
    font-size: 14px;
    line-height: 1.5;
    color: ${p => p.theme.secondaryText};
    margin: 0 0 16px;
`;

const CommandList = styled.dl<ThemeProps>`
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 4px 12px;
    margin: 0 0 24px;
    font-size: 14px;
    color: ${p => p.theme.secondaryText};

    dt {
        font-weight: 600;
        color: ${p => p.theme.primaryText};
    }

    dd {
        margin: 0;
    }
`;

const StartButton = styled.button<ThemeProps>`
    width: 100%;
    padding: 28px 0;
    font-size: 28px;
    font-weight: 700;
    font-family: inherit;
    border-radius: 12px;
    cursor: pointer;
    color: ${p => p.theme.successButtonForeground};
    background-color: ${p => p.theme.successButtonBackground};
    border: 2px solid ${p => p.theme.successButtonBorder};
`;

const VoicesLink = styled(Link)<ThemeProps>`
    align-self: center;
    margin-top: 16px;
    font-size: 14px;
    color: ${p => p.theme.secondaryText};
`;

const Status = styled.div<ThemeProps>`
    text-align: center;
    margin-bottom: 16px;
`;

const StatusLine = styled.div<ThemeProps>`
    font-size: 15px;
    color: ${p => p.theme.secondaryText};
`;

const StepLabel = styled.div<ThemeProps>`
    font-size: 30px;
    font-weight: 700;
    margin: 12px 0;
    min-height: 40px;
    color: ${p => p.theme.primaryText};
`;

const Heard = styled.div<ThemeProps>`
    min-height: 64px;
    margin-bottom: 20px;
    padding: 12px 16px;
    border-radius: 8px;
    text-align: center;
    background-color: ${p => p.theme.secondaryBackground};
    border: 1px solid ${p => p.theme.secondaryBorder};
`;

const HeardText = styled.div<ThemeProps>`
    font-size: 22px;
    font-weight: 600;
    color: ${p => p.theme.primaryText};
    overflow-wrap: anywhere;
`;

const HeardMeaning = styled.div<ThemeProps>`
    font-size: 14px;
    margin-top: 4px;
    color: ${p => p.theme.secondaryText};
`;

const Controls = styled.div`
    display: flex;
    gap: 12px;
`;

const BigButton = styled.button<ThemeProps>`
    flex: 1;
    min-height: 120px;
    font-size: 22px;
    font-weight: 700;
    font-family: inherit;
    border-radius: 12px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: ${p => p.theme.neutralButtonForeground};
    background-color: ${p => p.theme.neutralButtonBackground};
    border: 2px solid ${p => p.theme.neutralButtonBorder};
`;

const ButtonHint = styled.span`
    font-size: 14px;
    font-weight: 400;
    opacity: 0.85;
`;

const SecondaryControls = styled.div`
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 16px;
`;

const ErrorText = styled.p<ThemeProps>`
    color: ${p => p.theme.dangerColor};
    margin: 0 0 16px;
`;

const ResultCard = styled.div<ThemeProps>`
    border: 1px solid ${p => p.theme.secondaryBorder};
    background-color: ${p => p.theme.secondaryBackground};
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 10px;
`;

const ResultHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
`;

const ResultTitle = styled(Link)<ThemeProps>`
    font-size: 16px;
    font-weight: 600;
    color: ${p => p.theme.primaryText};
    overflow-wrap: anywhere;
`;

const Badge = styled.span<ThemeProps & { $tone: 'good' | 'bad' | 'neutral' }>`
    font-size: 13px;
    white-space: nowrap;
    color: ${p => p.$tone === 'good' ? p.theme.successColor : p.$tone === 'bad' ? p.theme.dangerColor : p.theme.secondaryText};
`;

const ItemLine = styled.div<ThemeProps>`
    font-size: 14px;
    color: ${p => p.theme.secondaryText};
    margin-top: 6px;
    overflow-wrap: anywhere;
`;

const GroupTitle = styled.h2<ThemeProps>`
    font-size: 16px;
    margin: 24px 0 10px;
    color: ${p => p.theme.primaryText};
`;

type Props = NavigationProps;

function stepLine(snapshot: CarSnapshot): string {
    const parts: string[] = [];
    if (snapshot.cardTotal > 0 && snapshot.cardNumber > 0)
        parts.push(`Card ${snapshot.cardNumber} / ${snapshot.cardTotal}`);
    if (snapshot.itemCount > 1 && snapshot.itemNumber > 0)
        parts.push(`item ${snapshot.itemNumber} / ${snapshot.itemCount}`);
    if (snapshot.group !== undefined)
        parts.push(snapshot.group);
    return parts.join(' · ');
}

function itemMark(isCorrect: boolean | undefined): string {
    return isCorrect === true ? '✓' : isCorrect === false ? '✗' : '–';
}

const Commands: FC = () => (
    <CommandList>
        {COMMANDS.map(([phrase, meaning]) => (
            <React.Fragment key={phrase}>
                <dt>{phrase}</dt>
                <dd>{meaning}</dd>
            </React.Fragment>
        ))}
    </CommandList>
);

const ResultRow: FC<{ result: CarCardResult; mode: CarMode }> = ({ result, mode }) => {
    let badge: { text: string; tone: 'good' | 'bad' | 'neutral' };
    if (result.skipped === true)
        badge = { text: 'could not be voiced', tone: 'bad' };
    else if (result.grade !== undefined)
        badge = result.saveFailed === true
            ? { text: `${GRADE_LABELS[result.grade]} · not saved`, tone: 'bad' }
            : { text: GRADE_LABELS[result.grade], tone: result.grade === 'good' ? 'good' : 'bad' };
    else
        badge = { text: mode === 'review' ? 'not rated' : 'listened', tone: 'neutral' };

    return (
        <ResultCard>
            <ResultHeader>
                <ResultTitle to={`/card/${result.cardId}`}>{result.title || 'Card'}</ResultTitle>
                <Badge $tone={badge.tone}>{badge.text}</Badge>
            </ResultHeader>
            {mode === 'review' && result.skipped !== true && result.items.map((item, index) => (
                <ItemLine key={index} data-testid="car-result-item">
                    {itemMark(item.isCorrect)} {item.label}
                    {item.heard !== undefined && <> — heard «{item.heard}»</>}
                </ItemLine>
            ))}
            {mode === 'review' && result.grade === undefined && (
                <ItemLine><Link to={`/review/${result.cardId}`}>Review now</Link></ItemLine>
            )}
        </ResultCard>
    );
};

const Summary: FC<{ snapshot: CarSnapshot; onRestart: () => void }> = ({ snapshot, onRestart }) => {
    const rated = snapshot.results.filter(result => result.grade !== undefined);
    const unrated = snapshot.results.filter(result => result.grade === undefined);

    return (
        <>
            {snapshot.error !== undefined && <ErrorText>{snapshot.error}</ErrorText>}
            {snapshot.results.length === 0 ? (
                <Message>No cards were played.</Message>
            ) : snapshot.mode === 'review' ? (
                <>
                    {rated.length > 0 && <GroupTitle>Rated ({rated.length})</GroupTitle>}
                    {rated.map(result => <ResultRow key={result.cardId} result={result} mode={snapshot.mode} />)}
                    {unrated.length > 0 && <GroupTitle>Not rated — still due ({unrated.length})</GroupTitle>}
                    {unrated.map(result => <ResultRow key={result.cardId} result={result} mode={snapshot.mode} />)}
                </>
            ) : (
                snapshot.results.map(result => <ResultRow key={result.cardId} result={result} mode={snapshot.mode} />)
            )}
            <PrimaryButton onClick={onRestart}>New session</PrimaryButton>
        </>
    );
};

export const Car: FC<Props> = () => {
    usePageTitle('Car · Memkeep');

    const [mode, setMode] = useState<CarMode>('review');
    const [limit, setLimit] = useState<CarLimit>(10);
    const [microphone, setMicrophone] = useState<string>(loadMicrophone);
    const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
    const [snapshot, setSnapshot] = useState<CarSnapshot | undefined>(undefined);
    const session = useRef<CarSession | undefined>(undefined);

    const refreshMicrophones = useCallback(() => {
        listMicrophones().then(setMicrophones, () => setMicrophones([]));
    }, []);

    useEffect(refreshMicrophones, [refreshMicrophones]);

    // Device names are only given out once the microphone was allowed.
    const revealMicrophones = useCallback(() => {
        navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
            stream.getTracks().forEach(track => track.stop());
            refreshMicrophones();
        }, () => undefined);
    }, [refreshMicrophones]);

    const chooseMicrophone = useCallback((deviceId: string) => {
        setMicrophone(deviceId);
        saveMicrophone(deviceId);
    }, []);

    const knownMicrophone = microphones.some(device => device.deviceId === microphone);
    const choices = microphones.filter(device => device.deviceId !== '' && device.deviceId !== 'default');

    const start = useCallback(() => {
        const next = new CarSession(mode, limit, createSpeechInput(knownMicrophone ? microphone : undefined), setSnapshot);
        session.current = next;
        next.start();
    }, [mode, limit, microphone, knownMicrophone]);

    const restart = useCallback(() => {
        session.current = undefined;
        setSnapshot(undefined);
    }, []);

    useEffect(() => () => session.current?.stop(), []);

    const running = snapshot !== undefined && snapshot.step !== 'finished';

    useEffect(() => {
        if (!running)
            return;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'ArrowRight')
                session.current?.command('next');
            else if (event.key === 'ArrowLeft')
                session.current?.command('prev');
            else if (event.key === ' ') {
                event.preventDefault();
                session.current?.togglePause();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [running]);

    return (
        <Page>
            <Content>
                <PageTitle>Car mode</PageTitle>

                {snapshot === undefined ? (
                    <>
                        <Section>
                            <Label>Mode</Label>
                            <Options>
                                <Option $active={mode === 'review'} onClick={() => setMode('review')}>Review</Option>
                                <Option $active={mode === 'listen'} onClick={() => setMode('listen')}>Listen only</Option>
                            </Options>
                        </Section>
                        <Section>
                            <Label>Cards</Label>
                            <Options>
                                {LIMITS.map(option => (
                                    <Option key={option} $active={limit === option} onClick={() => setLimit(option)}>
                                        {option === 'all' ? 'All due' : option}
                                    </Option>
                                ))}
                            </Options>
                        </Section>
                        <Section>
                            <Label>Microphone</Label>
                            <Options>
                                <Select
                                    data-testid="car-microphone"
                                    value={knownMicrophone ? microphone : ''}
                                    onChange={event => chooseMicrophone(event.target.value)}
                                >
                                    <option value="">Default</option>
                                    {choices.map((device, index) => (
                                        <option key={device.deviceId} value={device.deviceId}>
                                            {device.label || `Microphone ${index + 1}`}
                                        </option>
                                    ))}
                                </Select>
                                {choices.length > 0 && choices.every(device => device.label === '') && (
                                    <Option $active={false} onClick={revealMicrophones}>Show names</Option>
                                )}
                            </Options>
                        </Section>
                        <Hint>
                            Hands-free: each item is read out in full, the gap as a beep — say the missing word in the
                            right form. Or say a command, at any time:
                        </Hint>
                        <Commands />
                        <Hint>
                            {mode === 'listen' && 'Listen only plays the cards and saves nothing. '}
                            If the car&apos;s sound drops to call quality once the microphone is on, choose the
                            phone&apos;s own microphone above. Start before you drive.
                        </Hint>
                        <StartButton onClick={start}>Go</StartButton>
                        <VoicesLink to="/car/voices">Voices…</VoicesLink>
                    </>
                ) : running ? (
                    <>
                        <Status>
                            <StatusLine data-testid="car-position">{stepLine(snapshot)}</StatusLine>
                            <StepLabel data-testid="car-step">{snapshot.paused ? 'Paused' : STEP_LABELS[snapshot.step]}</StepLabel>
                            <StatusLine data-testid="car-speech">{SPEECH_LABELS[snapshot.speech]}</StatusLine>
                        </Status>
                        <Heard data-testid="car-heard">
                            {snapshot.partial !== undefined ? (
                                <HeardText>{snapshot.partial}…</HeardText>
                            ) : snapshot.heard !== undefined ? (
                                <>
                                    <HeardText>«{snapshot.heard.text}»</HeardText>
                                    <HeardMeaning>{snapshot.heard.meaning}</HeardMeaning>
                                </>
                            ) : (
                                <HeardMeaning>Say the answer or a command</HeardMeaning>
                            )}
                        </Heard>
                        <Commands />
                        <Controls>
                            <BigButton onClick={() => session.current?.command('prev')}>
                                ⏮<ButtonHint>repeat</ButtonHint>
                            </BigButton>
                            <BigButton onClick={() => session.current?.command('next')}>
                                ⏭<ButtonHint>next</ButtonHint>
                            </BigButton>
                        </Controls>
                        <SecondaryControls>
                            <PrimaryButton onClick={() => session.current?.togglePause()}>
                                {snapshot.paused ? 'Resume' : 'Pause'}
                            </PrimaryButton>
                            <PrimaryButton onClick={() => session.current?.stop()}>Finish</PrimaryButton>
                        </SecondaryControls>
                    </>
                ) : (
                    <Summary snapshot={snapshot} onRestart={restart} />
                )}
            </Content>
        </Page>
    );
};
Car.displayName = 'Car';
